import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {Map as ImmutableMap} from 'immutable';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getOptionFromValueTimePeriod, unitsOptions} from '../../PlanTemplates/ACC/benefitDefaults';
import {formatCurrencyValue} from './ACCBenefits';
import {getEmergencyRoomServices} from './PropertyPaths/propertyPathACC';

const EmergencyRoomSvcs = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);
  const {selectedPlan, selectedProduct} = state.quoteForm;
  const propertyPaths = getEmergencyRoomServices(selectedPlan?.planIndex || 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'emergency-room-svcs-row-1'},
      items: [
        {
          key: 'emergency-room-svcs-units',
          element: (
            <QuoteFormElement
              elementType="Select"
              type="number"
              label="Units"
              required
              disabled
              options={unitsOptions}
              value={getQuoteFieldValue<number>(
                propertyPaths.emergencyRoomServicesDetails.unitsOfEmergencyRoomServices
              )}
              id={'unitsOfEmergencyRoomServices'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'emergency-room-svcs-row-2'},
      items: [
        {
          key: 'emergency-room-svcs-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Incurral Period (Days)"
              value={getQuoteFieldValue<number>(propertyPaths.emergencyRoomServicesDetails.incurralPeriod)}
              disabled
              required
              id={'incurralPeriod'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'emergency-room-svcs-customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              checked={getQuoteFieldValue<boolean>(propertyPaths.emergencyRoomServicesDetails.customizeBenefitAmount)}
              id={'customizeBenefitAmount'}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'emergency-room-svcs-row-3'},
      items: [
        {
          key: 'emergency-room-svcs-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              adornment="money"
              required
              value={formatCurrencyValue(
                getQuoteFieldValue<number>(propertyPaths.emergencyRoomServicesDetails.benefitAmount)
              )}
              id={'accidentEmergencyRoomServices.benefitAmount'}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'emergency-room-svcs-payable-per-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCEmergencyRoomServicesBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.emergencyRoomServicesDetails.maxBenefitNumber)}
              disabled
              required
              id={'accidentEmergencyRoomServices.maxBenefitNumber'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'emergency-room-svcs-row-4'},
      items: [
        {
          key: 'emergency-room-svcs-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.emergencyRoomServicesDetails.timePeriodCd)}
              options={getOptionFromValueTimePeriod(
                getQuoteFieldValue<string>(propertyPaths.emergencyRoomServicesDetails.timePeriodCd) ?? ''
              )}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AccidentEmergencyRoomServicesBlock = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const state = useSelector((state: ReducerState) => state);
  const {selectedPlan, selectedProduct} = state.quoteForm;
  const propertyPaths = useMemo(() => getEmergencyRoomServices(selectedPlan?.planIndex || 0, 0), [
    selectedPlan?.planIndex,
    selectedProduct?.code,
  ]);

  const isGVA6Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) == 'GVA6';

  const accidentEmergencyRoomSvcs: ConditionalSection[] = [
    {
      Component: EmergencyRoomSvcs,
      key: 'EmergencyRoomSvcs',
      label: 'EMERGENCY ROOM SERVICES',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isEmergencyRoomServicesApplied),
    },
  ];

  const GAI7Title = 'Accident Treatment and Urgent Care - Emergency Room Services';
  const GVA6Title = 'Emergency Room Services Rider';

  return (
    <FormWrapper title={isGVA6Plan ? GVA6Title : GAI7Title} collapsible withSeparator>
      <QuoteFormConditionalSection sections={accidentEmergencyRoomSvcs} />
    </FormWrapper>
  );
};
